import { Box, Button, createStyles, makeStyles, Typography } from '@material-ui/core';
import cn from 'classnames';
import moment from 'moment';
import * as React from 'react';
import { TranquiTheme } from '../../../containers/App/theme';
import DebtNegDetailsSelectItem from '../../../containers/DebtsNegRoot/DebtNegDetailsSelectItem';
import MarkdownRender from '../../../dictionaries/MarkdownRender';
import RenderText from '../../../dictionaries/RenderText';
import useDictionary from '../../../dictionaries/context/useDictionary';
import { BankType, NegotiationOption, NegotiationOptionAlternative, NegotiationType, OutDebt } from '../../../generated/graphql';
import { getNegBooleans } from '../../../utils/getNegBooleans';
import { getNegotiationOptionAlternativeTypeLabel } from '../../../utils/getNegotiationOptionAlternativeTypeLabel';
import { pluralize } from '../../../utils/pluralize';
import thousandSeparator from '../../../utils/thousandSeparator';
import Alert from '../../Alert/Alert';
import Collapse from '../../Collapse/Collapse';

interface SelectAlternativeProps {
  debt: OutDebt;
  alternatives: NegotiationOptionAlternative[] | undefined;
  negOption: NegotiationOption | null;
  selectedAlternative: NegotiationOptionAlternative | null;
  setSelectedAlternative: React.Dispatch<React.SetStateAction<NegotiationOptionAlternative | null>>;
  autoSelect?: boolean;
  className?: string;
  // eslint-disable-next-line react/no-unused-prop-types
  onChangeSelectedOption?: (option: Pick<NegotiationOption, 'type'>) => void;
}

const SelectAlternative: React.FC<SelectAlternativeProps> = (props) => {
  const {
    debt, negOption, alternatives: alternativesProps,
    selectedAlternative,
    setSelectedAlternative,
    autoSelect, className,
  } = props;

  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  const classes = useStyles();
  const { gT } = useDictionary();
  const [ showAllAlternatives, setShowAllAlternatives ] = React.useState(false);

  const isDav = debt.bank === BankType.BancoDavivienda;
  const isBogota = debt.bank === BankType.BancoDeBogota;
  const isScotiabank = debt.bank === BankType.Scotiabank;
  const isACPK = negOption?.type === NegotiationType.PenaltyPortfolioPaymentAgreement;
  const isDavACPK = isDav && isACPK;

  const alternatives = (alternativesProps || [])?.filter((alt) => alt.available);
  const onHandleChangeAlternativeSelected = (value: NegotiationOptionAlternative) => () => {
    setSelectedAlternative((prev) => (prev === value ? null : value));
  };

  React.useEffect(() => {
    if (!alternatives || !autoSelect) return;
    if (alternatives.length === 1 && !isDavACPK) setSelectedAlternative(alternatives[0]);
    if (alternatives.length === 0) setSelectedAlternative(null);
  }, [ alternatives, setSelectedAlternative, autoSelect, isDavACPK ]);

  const visibleAlternatives = alternatives
    .filter((alt) => showAllAlternatives || alt.hidden !== true);

  const hasHiddenAlternatives = alternatives.filter((alt) => alt.hidden).length > 0;

  if (!negOption) return null;
  const { isPartialPaymentPromise } = getNegBooleans(debt, negOption);

  if (!debt
    || !alternatives
    || !alternativesProps
    || (alternatives.length === 1 && !isDavACPK && !isPartialPaymentPromise)
    || alternatives.length === 0
  ) return null;

  const getTitle = ({ term, type, value }: NegotiationOptionAlternative) => (
    isPartialPaymentPromise && !!type
      ? `${getNegotiationOptionAlternativeTypeLabel(type, debt.bank)} $${thousandSeparator(value)}`
      : `${term}  \ncuota${pluralize(term)}`
  );

  const getAlternativeAlertText = () => {
    if (!selectedAlternative?.type || (isDav && !debt.exm)) return 'selectAlternative_alert_default_partialPaymentPromise';
    const extra = isDav ? `_${debt.exm}` : '';
    return `selectAlternative_alert_${selectedAlternative.type}_partialPaymentPromise${extra}`;
  };

  return (
    <Box
      className={cn(classes.alternatives, className)}
      data-test={`cy-negOption-${negOption.type}-alternatives`}
    >
      <Collapse in={!alternativesProps.every((alt) => alt.available)}>
        <Alert
          color="warning"
          filled
          icon="info"
          iconColor="warning"
          contentClassName={classes.alertBonus}
          className="mb-2"
        >
          Para ver opciones de menor plazo, debes aumentar el abono.
        </Alert>
      </Collapse>
      {
        isPartialPaymentPromise && (
        <Alert
          color="gray"
          icon="info"
          iconColor="gray"
          contentClassName={classes.alertBonus}
          className="mb-2"
        >
          <RenderText isMarkdown textKey="newPaymentTerms_paymentDate_alert_partialPaymentPromise" context={{ maxPaymentDate: negOption.settings!.maxPaymentDate }} />
        </Alert>
        )
      }
      {
        visibleAlternatives.length > 1 && (
        <>
          <Typography className={cn(classes.alternativeTitle, 'mb-2')}>
            <RenderText isMarkdown textKey={`newPaymentTerms_alternatives_title${isPartialPaymentPromise ? '_partialPaymentPromise' : ''}`} />
          </Typography>
          <Box
            className={cn(classes.assigmentsBoxItems,
              isPartialPaymentPromise && 'isPartialPaymentPromise')}
            data-test={`cy-alternativesContent-${negOption.type}`}
          >
            {visibleAlternatives.sort((a, b) => (a.term - b.term)).map((alternative) => {
              const { term, value, type } = alternative;
              const selected = selectedAlternative?.term === term
                && selectedAlternative?.type === type;
              return (
                <DebtNegDetailsSelectItem
                  key={term + value}
                  title={<MarkdownRender text={getTitle(alternative)} />}
                  selected={selected}
                  onClick={onHandleChangeAlternativeSelected(alternative)}
                  className={cn(classes.selectedItem, isPartialPaymentPromise && 'isPartialPaymentPromise')}
                  dataTest={`cy-negOption-${negOption.type}-${term}`}
                />
              );
            })}
          </Box>
        </>
        )
      }
      {hasHiddenAlternatives && (
        <Button
          variant="text"
          color="secondary"
          className={cn(classes.explore)}
          onClick={() => setShowAllAlternatives((prev) => !prev)}
        >
          {!showAllAlternatives ? 'Ver más cuotas' : 'Ver menos cuotas'}
        </Button>
      )}
      {
        (isPartialPaymentPromise && (isDav || isBogota || isScotiabank)) && (
        <Alert
          color="secondary"
          icon="info"
          iconColor="secondary"
          title={gT('alert_PaymentPromise_confirmation_title')}
          contentClassName={classes.alertBonus}
          className="mb-2"
        >
          <RenderText
            isMarkdown
            textKey={getAlternativeAlertText()}
            context={{
              paymentDate: debt.paymentDate!,
              daysBehind: debt.daysBehind!,
              lastBilledDay: moment().subtract(2, 'days').toDate(),
            }}
          />
        </Alert>
        )
      }
    </Box>
  );
};

const useStyles = makeStyles((theme: TranquiTheme) => createStyles({
  alertBonus: {
    color: theme.palette.indigo.main,
    '& ul': {
      paddingInlineStart: theme.spacing(2.5),
    },
  },
  alternatives: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: theme.palette.common.white,
    padding: theme.spacing(2),
  },
  alternativeTitle: {
    ...theme.typography.body1,
    color: theme.palette.indigo.main,
    fontWeight: 'bold',
  },
  assigmentsBoxItems: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))',
    rowGap: `${theme.spacing(2)}px`,
    columnGap: `${theme.spacing(2)}px`,
    marginBlockEnd: `${theme.spacing(2)}px`,
    '&.isPartialPaymentPromise': {
      [theme.breakpoints.down('sm')]: {
        gridTemplateColumns: 'repeat(auto-fit, minmax(100%, 1fr))',
      },
    },
  },
  assigmentsBoxItemsWithMoreSpace: {
    rowGap: `${theme.spacing(4)}px`,
  },
  selectedItem: {
    borderRadius: theme.spacing(3),
    [theme.breakpoints.up('md')]: {
      maxInlineSize: '130px',
    },
    '&>div': {
      blockSize: `${theme.spacing(10)}px`,
    },
    '&.isACPK:first-child, &.isACPK:last-child': {
      '& .wrapper': {
        boxShadow: '0px -4px 4px rgba(0, 0, 0, 0.1)',
      },
      '&::before': {
        content: '"Mayor descuento"',
        position: 'absolute',
        insetBlockEnd: '95%',
        inlineSize: '100%',
        backgroundColor: theme.palette.primary.main,
        ...theme.typography.xsmall,
        color: theme.palette.pearl.main,
        fontWeight: 'bold',
        padding: theme.spacing(0.5, 0, 1, 0),
        borderRadius: theme.spacing(1, 1, 0, 0),
        zIndex: 1,
      },
    },
    '&.isACPK:last-child': {
      '&.isACPK::before': {
        content: '"Menor cuota"',
        backgroundColor: '#FFB95A',
      },
    },
  },
  explore: {
    fontWeight: 'bold',
    textTransform: 'initial',
    textDecoration: 'underline',
  },
}));

export default SelectAlternative;
